export type VolOceanSubmissionSource = 'VolOcean Website' | 'VolOcean Launch Invite';

export const VOLOCEAN_SUBMISSION_SOURCES = {
  website: 'VolOcean Website',
  launchInvite: 'VolOcean Launch Invite'
} as const satisfies Record<string, VolOceanSubmissionSource>;

export const VOLOCEAN_PUBLIC_URLS = {
  launch: 'https://volocean.com/launch'
} as const;

export const VOLOCEAN_WEBSITE_URL = 'https://volocean.com/';

export const VOLOCEAN_EVENT = {
  title: 'VOLOCEAN Launch',
  dateDisplay: 'Thursday, 21 May 2026',
  timeDisplay: '18:00 – 20:00',
  venueShort: 'Work Club',
  venueAddress: 'Level 32, 200 George St, Sydney',
  description: 'Join Ocean VC and VOLOCEAN for the VOLOCEAN launch event.',
  // UTC equivalents of Australia/Sydney 18:00-20:00 on 21 May 2026 (AEST = UTC+10)
  startUtc: '20260521T080000Z',
  endUtc: '20260521T100000Z',
  startIso: '2026-05-21T08:00:00Z',
  endIso: '2026-05-21T10:00:00Z',
  icsFileName: 'volocean-launch.ics',
} as const;

export const getGoogleCalendarUrl = (): string => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: VOLOCEAN_EVENT.title,
    dates: `${VOLOCEAN_EVENT.startUtc}/${VOLOCEAN_EVENT.endUtc}`,
    details: VOLOCEAN_EVENT.description,
    location: `${VOLOCEAN_EVENT.venueShort}, ${VOLOCEAN_EVENT.venueAddress}`
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const getMicrosoftOutlookUrl = (): string => {
  const params = new URLSearchParams({
    subject: VOLOCEAN_EVENT.title,
    startdt: VOLOCEAN_EVENT.startIso,
    enddt: VOLOCEAN_EVENT.endIso,
    body: VOLOCEAN_EVENT.description,
    location: `${VOLOCEAN_EVENT.venueShort}, ${VOLOCEAN_EVENT.venueAddress}`,
    path: '/calendar/action/compose',
    rru: 'addevent'
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

export const getMicrosoftOffice365Url = (): string => {
  const params = new URLSearchParams({
    subject: VOLOCEAN_EVENT.title,
    startdt: VOLOCEAN_EVENT.startIso,
    enddt: VOLOCEAN_EVENT.endIso,
    body: VOLOCEAN_EVENT.description,
    location: `${VOLOCEAN_EVENT.venueShort}, ${VOLOCEAN_EVENT.venueAddress}`,
    rru: 'addevent'
  });
  return `https://outlook.office.com/calendar/action/compose?${params.toString()}`;
};

export const generateIcsContent = (): string => {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//VOLOCEAN//EN',
    'BEGIN:VEVENT',
    `DTSTART:${VOLOCEAN_EVENT.startUtc}`,
    `DTEND:${VOLOCEAN_EVENT.endUtc}`,
    `SUMMARY:${VOLOCEAN_EVENT.title}`,
    `DESCRIPTION:${VOLOCEAN_EVENT.description}`,
    `LOCATION:${VOLOCEAN_EVENT.venueShort}\\, ${VOLOCEAN_EVENT.venueAddress}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

export interface VolOceanWebsiteEnquiry {
  name: string;
  company: string;
  email: string;
  message: string;
}

export interface VolOceanLaunchRsvp {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  roleTitle: string;
  attendanceStatus: 'attending' | 'not_attending';
  notes: string;
}

export type VolOceanRsvpAdminStatus = 'new' | 'contacted' | 'closed';

export interface VolOceanRsvpRecord extends VolOceanLaunchRsvp {
  id?: string;
  fullName: string;
  createdAt: Date;
  source: 'volocean_launch_invite';
  leadSource: typeof VOLOCEAN_SUBMISSION_SOURCES.launchInvite;
  leadSourceType: 'campaign';
  status: VolOceanRsvpAdminStatus;
  eventDate: string;
  eventTime: string;
  legacyLeadId?: string;
}

export interface VolOceanValueItem {
  title: string;
  body: string;
  iconLabel: string;
  iconPath: string;
}

export type VolOceanServiceIconKey = 'advisory' | 'partnerships' | 'investment' | 'edge';

export interface VolOceanServiceItem {
  title: string;
  tagline: string;
  iconKey: VolOceanServiceIconKey;
  imagePath: string;
  paragraphs: string[];
  cta?: { label: string; href: string };
}

export interface VolOceanTeamMember {
  name: string;
  title: string;
  location: string;
  profileImages: {
    bw: string;
    colour: string;
    alt: string;
  };
  bio: string[];
  /** CSS object-position when cover-crop focal point does not match object-center (e.g. tight portrait). */
  profileImageObjectPosition?: string;
  /** Thumbnail-only crop tuning for source portraits with different face heights. */
  profileImageThumbTransform?: string;
  profileImageThumbTransformOrigin?: string;
}

export interface VolOceanPortfolioItem {
  id: string;
  name: string;
  logoPath: string;
  logoAlt: string;
  body: string;
  websiteUrl: string;
  /** Tailwind size classes for this logo — overrides the default sizing when set. */
  logoClassName?: string;
}

export interface VolOceanLocationItem {
  country: string;
  city: string;
  addressLines: string[];
  coordinates: {
    lat: number;
    lon: number;
  };
}

export interface VolOceanPressReleaseQuote {
  quote: string;
  name: string;
  title: string;
}

export interface VolOceanPressRelease {
  eyebrow: string;
  title: string;
  dateline: string;
  introParagraphs: string[];
  bodyParagraphs: string[];
  quotes: VolOceanPressReleaseQuote[];
  integratedModelItems: string[];
  strategicOutcomeItems: string[];
  closingParagraphs: string[];
}

/**
 * Tailwind class bundles for full wordmark SVGs (`volocean-black.svg` / `volocean-white.svg`).
 * ViewBox is ~2144×240 (~9:1). Setting only `h-*` makes width ~9× height and blows up nav bars.
 * Always combine height with `max-w-*` + `object-contain`.
 *
 * Legacy reference: `logo192.png` was nearly square and rendered around **h-8 / md:h-10** in nav —
 * a **~32–40px-tall × ~32–40px-wide** visual block. Wordmarks are wide; presets below cap width so
 * chrome stays in that ballpark horizontally without dominating the bar (see MASTER_PLAN sizing note).
 *
 * `navBar` is the exception: a ~100px cap crushed the 9:1 mark so the globe aliased. Cap at ~120–128px
 * with `geometricPrecision` so the mark stays readable without dominating the bar.
 */
export const VOLOCEAN_WORDMARK_IMG_CLASS = {
  navBar:
    'block h-6 w-auto max-w-[120px] md:h-7 md:max-w-[128px] object-contain object-left shrink-0 [shape-rendering:geometricPrecision]',
  authCard:
    'h-7 w-auto max-w-[92px] object-contain sm:h-8 sm:max-w-[100px]',
  authAdmin:
    'h-8 w-auto max-w-[100px] object-contain',
  applicationShell:
    'h-10 w-auto max-w-[128px] sm:h-11 sm:max-w-[140px] md:h-12 md:max-w-[152px] object-contain',
  waitingApproval:
    'h-10 w-auto max-w-[116px] object-contain mx-auto mb-6 drop-shadow-lg',
  investorOnboarding:
    'h-9 sm:h-10 w-auto max-w-[116px] sm:max-w-[128px] mx-auto mb-4 sm:mb-6 object-contain',
  errorBoundary:
    'h-12 w-auto max-w-[140px] mx-auto mb-4 animate-pulse object-contain',
  landingHeader:
    'h-9 sm:h-10 w-auto max-w-[116px] sm:max-w-[132px] object-contain',
  partnershipHeader:
    'branding-logo object-contain max-h-[64px] w-auto max-w-[132px]',
  partnershipSection:
    'h-12 w-auto max-w-[148px] object-contain opacity-90',
  elevateHeader:
    'h-10 sm:h-11 w-auto max-w-[min(78vw,240px)] object-contain object-center',
  elevateHeaderCompact:
    'h-9 sm:h-10 w-auto max-w-[min(72vw,220px)] object-contain object-center',
  footerLarge:
    'h-16 sm:h-20 w-auto max-w-[min(94vw,340px)] mx-auto object-contain opacity-90',
  footerMedium:
    'h-14 sm:h-16 w-auto max-w-[min(92vw,280px)] mx-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300',
  footerAlfred:
    'h-14 sm:h-16 w-auto max-w-[min(92vw,280px)] mx-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300',
} as const;

/** Temporary homepage hero line (merger domain cutover). Set enabled false or remove after ~Aug 2026. */
export const VOLOCEAN_HERO_MERGER_ANNOUNCEMENT = {
  enabled: true,
  line: 'Voluntas Group and Ocean VC are now'
} as const;

export const VOLOCEAN_ASSETS = {
  logoWhite: '/volocean-assets/logos/volocean-white.svg',
  logoBlack: '/volocean-assets/logos/volocean-black.svg',
  logoNavy: '/volocean-assets/logos/volocean-navy.svg',
  oMarkWhite: '/volocean-assets/logos/volocean-o-white.svg',
  // Natural Earth public-domain world geometry, simplified as a static SVG to avoid a map library in the landing bundle.
  worldMap: '/volocean-assets/maps/world-map-natural-earth.svg',
  heroVideo: '/volocean-assets/media/volocean-hero-no-logo.mp4',
  heroPoster: '/volocean-assets/media/volocean-hero-poster.png',
  capitalRaiseHeroVideo: '/volocean-assets/media/capital-raise-hero.mp4',
  capitalRaiseHeroPoster: '/volocean-assets/media/capital-raise-hero-poster.jpg',
  growthHeroVideo: '/volocean-assets/media/growth-hero.mp4',
  growthHeroPoster: '/volocean-assets/media/growth-hero-poster.jpg',
  launchInvite: '/volocean%20assets/Launch%20invitation%20-%20beige%20brown.png',
  // ARCHIVED 27 Aug 2026. Jason's IE phase diagrams. Do not wire into the journey page unless
  // product asks to retry. Files stay in public/volocean-assets/ie-phases/.
  iePhaseOversight: '/volocean-assets/ie-phases/oversight.png',
  iePhaseOptimisation: '/volocean-assets/ie-phases/optimisation.png',
  iePhaseExecution: '/volocean-assets/ie-phases/execution.png'
} as const;

export const VOLOCEAN_PRESS_RELEASE: VolOceanPressRelease = {
  eyebrow: 'Formal Announcement',
  title: 'Voluntas Group and Ocean VC Announce Strategic Merger to Create VOLOCEAN — Global Advisory and Investment Platform',
  dateline: 'AUCKLAND, NEW ZEALAND & SYDNEY, AUSTRALIA (1 May 2026)',
  introParagraphs: [
    'Voluntas Group and Ocean VC today announced their strategic merger, forming VOLOCEAN — a globally integrated advisory, investment, and venture growth business.',
    'The merger brings together two highly complementary organisations with aligned long-term philosophies around founder support, capital markets participation, disciplined execution, and enterprise value creation.'
  ],
  bodyParagraphs: [
    'VOLOCEAN combines Voluntas Group’s globally connected advisory practice, headquartered in Auckland and led by Founder and Managing Director Jason Holdsworth, with Ocean VC’s investment and venture capability — supporting founder-led technology companies across growth strategy, international expansion, capital raising, governance, and liquidity events. The combined practice operates across New Zealand, Australia, Asia, the United Kingdom, and the United States, with a strong focus on execution-led advisory and long-term founder alignment.',
    'Ocean VC, headquartered in Sydney and led by Founder and Managing Partner Will Lawson, is an investment, funds management, and advisory business with deep founder-led experience across structured capital, institutional investment, private markets execution, and strategic transaction advisory. Ocean VC brings strong capability in capital structuring, institutional partnerships, and hands-on advisory support for founders navigating growth, capital events, and complex transactions.',
    'The merger creates a materially stronger global platform positioned to support founders, investors, family offices, institutional partners, and portfolio companies across the full company lifecycle — from early-stage growth and expansion through to capital events, acquisitions, and exits — delivered through an AI-enriched, single integrated platform spanning advisory, partnerships, competitive edge creation, and investments.',
    'This platform-led approach enables deeper insight, faster execution, and tighter alignment across advisory mandates, strategic partnerships, edge development, and capital deployment.',
    'The combined group intends to leverage the strengths of both organisations to build a broader global ecosystem supporting high-growth companies, investment partners, and strategic stakeholders.'
  ],
  quotes: [
    {
      quote: 'This merger brings together two highly complementary businesses to create a globally connected advisory and investment group delivering exceptional outcomes for founders, investors, and partners.',
      name: 'Jason Holdsworth',
      title: 'Founder and Managing Director, VOLOCEAN'
    },
    {
      quote: 'Together, Ocean VC and VOLOCEAN are creating a unique private markets business with the capability, relationships, international reach, and technology foundation to build something very significant over the long term.',
      name: 'Will Lawson',
      title: 'Founder and Managing Partner, Ocean VC'
    }
  ],
  integratedModelItems: [
    'Advisory: Strategic advisory and growth execution, capital raising and structured finance, international expansion and market entry, and M&A advisory and transaction execution',
    'Edge: Technology enablement, AI-driven insights, and venture studio initiatives designed to create sustainable competitive advantage',
    'Investment: Venture capital, venture debt, private credit, and property strategies across internal funds, special purpose vehicles, and funds-of-funds partnerships',
    'Partnerships: An integrated ecosystem of legal, accounting, administration, technology, human resources, fintech services, and investment management partners'
  ],
  strategicOutcomeItems: [
    'enhanced capital markets and funds management capability',
    'deeper advisory and execution capacity',
    'expanded global reach across ANZ, Asia, the UK, and the US',
    'improved access to institutional and strategic investor networks',
    'stronger portfolio company support across growth stages',
    'increased ability to facilitate international expansion and partnerships',
    'scalable, AI-enabled infrastructure designed for long-term enterprise growth and value creation'
  ],
  closingParagraphs: [
    'The transaction now progresses into final integration planning and operational alignment, with further announcements regarding group structure, leadership, and strategic initiatives to be shared as integration advances.'
  ]
};

export const VOLOCEAN_VALUES: VolOceanValueItem[] = [
  {
    title: 'Our Vision',
    iconLabel: 'Vision',
    iconPath: '/volocean-assets/icons/vision.png',
    body: 'To create a fair, capitalised ecosystem where investors, founders, and advisors work in partnership-sharing value, not extracting it-so that individuals and organisations can grow, innovate, and lead with purpose.'
  },
  {
    title: 'Our Mission',
    iconLabel: 'Mission',
    iconPath: '/volocean-assets/icons/mission.png',
    body: 'We are building an integrated platform of entities and people who co-invest, co-create, and co-exit. Through shared services, aligned equity structures, and global operational support, we connect the right capital, capability, and community to help ventures grow sustainably-and with intention.'
  },
  {
    title: 'Our Values',
    iconLabel: 'Values',
    iconPath: '/volocean-assets/icons/values.png',
    body: 'VOLOCEAN values inclusive collaboration, transparency, fairness, and progressive thinking. We foster respectful, open relationships and challenge inequality to drive sustainable impact across communities and industries.'
  }
];

export const VOLOCEAN_SERVICES: VolOceanServiceItem[] = [
  {
    title: 'Advisory',
    tagline: 'Global advisory, built to accelerate growth and deliver superior exit outcomes.',
    iconKey: 'advisory',
    imagePath: '/volocean-assets/media/Advisory_sketch.png',
    paragraphs: [
      'We provide strategic and corporate advisory services to founder-led technology companies, focused on accelerating growth and maximising enterprise value through to liquidity events. Operating across New Zealand, Australia, Hong Kong, the United States, and the United Kingdom, we deliver coordinated international expansion with on-the-ground execution capability.',
      'Our typical engagement begins with strategy and capital formation, refining positioning, strengthening investor readiness, and executing capital raises. The true value, however, is realised post-capital, where we deploy our platform, partnerships, and global operating capability to scale revenue, expand into new markets, and build the foundations for successful exit outcomes.',
      'This integrated model ensures capital is not only secured but effectively deployed to drive sustainable growth and valuation uplift.'
    ],
    cta: { label: 'Learn more', href: '/elevate' }
  },
  {
    title: 'Partnerships',
    tagline: 'Strategic relationships that unlock distribution, capital, and growth.',
    iconKey: 'partnerships',
    imagePath: '/volocean-assets/media/partnerships_asset.png',
    paragraphs: [
      'We build and activate high-value relationships across a global network of investors, corporates, and operating partners. These partnerships are designed to accelerate market access, generate revenue, and support long-term capital outcomes.',
      'Our approach is structured and execution-focused - identifying, qualifying, and activating the right counterparties to deliver measurable results. From channel partnerships to strategic alliances and investor access, we ensure relationships translate into tangible commercial outcomes.',
      'Partnerships are not an adjunct, they are a core driver of scalable growth.'
    ],
    cta: { label: 'Become A Partner', href: '/partners' }
  },
  {
    title: 'Investment',
    tagline: 'Active capital deployment, structured for performance and long-term value creation.',
    iconKey: 'investment',
    imagePath: '/volocean-assets/media/investment_asset.png',
    paragraphs: [
      'We operate a structured investment platform designed to deploy capital into high-growth, productivity-enhancing opportunities, with a focus on technology, innovation, and scalable intellectual property.',
      'Capital is deployed through regulated investment vehicles, including managed funds, partnerships, and special purpose structures, ensuring robust governance, compliance, and alignment with long-term investor objectives. Investment decisions are disciplined, centrally governed, and supported by deep sector expertise and global deal origination capability.',
      'Our approach prioritises active investment over passive allocation, targeting businesses that drive revenue growth, skilled employment, and export expansion. This includes direct equity investments, co-investment opportunities, and exposure to curated deal flow across multiple jurisdictions.',
      'The platform is designed to support both concentrated growth strategies and diversified portfolios, providing flexibility across investment mandates while maintaining strict oversight, reporting, and capital discipline.',
      'By integrating advisory, venture creation, and capital execution, we ensure that capital is not only deployed effectively but actively managed to drive measurable outcomes and superior returns.'
    ]
  },
  {
    title: 'Edge',
    tagline: 'Building and scaling ventures at the intersection of technology, capital, and execution.',
    iconKey: 'edge',
    imagePath: '/volocean-assets/media/edge_asset.png',
    paragraphs: [
      'We originate, build, and scale technology ventures using a combination of AI-enabled tools, global expertise, and hands-on execution capability.',
      'This includes validating opportunities, developing market-ready products, and preparing companies for growth and capital. Each venture is built with a clear focus on scalability, commercial viability, and exit potential.',
      'Fully integrated with advisory and investment capabilities, this function ensures a continuous pipeline of high-quality opportunities and long-term value creation across the platform.'
    ]
  }
];

export const VOLOCEAN_PORTFOLIO: VolOceanPortfolioItem[] = [
  {
    id: 'fascin8foods',
    name: 'Fascin8foods',
    logoPath: '/volocean-assets/clients/fascin_foods.png',
    logoAlt: 'Fascin8foods logo',
    body: 'Australian food innovation company developing mushroom-based plant protein products focused on sustainable, clean-label alternatives for the global food and retail sectors.',
    websiteUrl: 'https://fascin8foods.com.au/froom-retail/',
    logoClassName: 'max-h-16 w-[65%]',
  },
  {
    id: 'xcontrol-golf',
    name: 'Xcontrol Golf',
    logoPath: '/volocean-assets/clients/x_control.png',
    logoAlt: 'Xcontrol Golf logo',
    body: 'Sports technology company developing performance golf grips designed to improve clubface awareness, control and player feedback for golfers seeking faster skill development.',
    websiteUrl: 'https://xcontrolgolf.com/',
    logoClassName: 'max-h-20 w-[55%]',
  },
  {
    id: 'securelink-networks',
    name: 'Securelink Networks',
    logoPath: '/volocean-assets/clients/securelink_networks.png',
    logoAlt: 'Securelink Networks logo',
    body: 'Cybersecurity and connectivity infrastructure company delivering secure enterprise networking, cloud connectivity and virtual secure network solutions across Australia and Southeast Asia.',
    websiteUrl: 'https://www.securelinknetworks.net/',
    logoClassName: 'max-h-12 w-[88%]',
  },
  {
    id: 'sherlok',
    name: 'Sherlok',
    logoPath: '/volocean-assets/clients/sherlock.png',
    logoAlt: 'Sherlok logo',
    body: 'Automated rate tracking, repricing and application platform for mortgage brokers, enabling scalable client retention through streamlined refinancing, repricing and proactive loan monitoring.',
    websiteUrl: 'https://www.sherlok.com.au/',
    logoClassName: 'max-h-14 w-[80%]',
  },
  {
    id: 'pulse-bpi',
    name: 'Pulse BPI',
    logoPath: '/volocean-assets/clients/pulse_bpi.png',
    logoAlt: 'Pulse BPI logo',
    body: 'A data-driven, tech platform that enables building management visibility, transparency, efficiency, sustainability, and insurance management.',
    websiteUrl: 'https://pulsebpi.com/',
    logoClassName: 'max-h-14 w-[80%]',
  },
  {
    id: 'medtech-labs-group',
    name: 'MedTech Labs Group',
    logoPath: '/volocean-assets/clients/medtech_labs_group.jpg',
    logoAlt: 'MedTech Labs Group logo',
    body: 'Medtech Labs Group is the exclusive distributor of Nurochek Concussion device in Australia, New Zealand and South Africa and a significant investor in HeadSafe (the owner of Nurochek).',
    websiteUrl: 'https://qa.medtechlabsgroup.com/',
    logoClassName: 'max-h-24 w-[88%]',
  },
  {
    id: 'alfredai',
    name: 'AlfredAI',
    logoPath: '/volocean-assets/clients/alfredai.png',
    logoAlt: 'AlfredAI logo',
    body: 'The AI operating layer for mid-market firms. Execution, accountability, and scale — without adding headcount.',
    websiteUrl: 'https://new.alfredai.bot/',
    logoClassName: 'max-h-16 w-[70%]',
  },
  {
    id: 'gogoprop',
    name: 'GoGoProp',
    logoPath: '/volocean-assets/clients/gogoprop.png',
    logoAlt: 'GoGoProp logo',
    body: 'Property lending platform enabling fast, flexible real estate financing solutions.',
    websiteUrl: 'https://www.gogoprop.com/',
    logoClassName: 'max-h-14 w-[75%]',
  },
  {
    id: 'gheorg',
    name: 'Gheorg',
    logoPath: '/volocean-assets/clients/gheorg-logo.png',
    logoAlt: 'Gheorg logo',
    body: 'A virtual mental health app designed to support children under 12 facing anxiety, depression, and other mental health challenges.',
    websiteUrl: 'https://www.gheorg.com/',
    logoClassName: 'max-h-20 w-[55%]',
  },
  {
    id: 'open-pantry',
    name: 'Open Pantry',
    logoPath: '/volocean-assets/clients/open-pantry-logo.png',
    logoAlt: 'Open Pantry logo',
    body: 'Revolutionising the hospitality industry by offering a streamlined, digital ordering platform designed specifically for restaurants and wholesale suppliers.',
    websiteUrl: 'https://www.theopenpantry.com/',
    logoClassName: 'max-h-12 w-[85%]',
  },
  {
    id: 'schnuzzle',
    name: 'Schnuzzle',
    logoPath: '/volocean-assets/clients/schnuzzle-logo.jpg',
    logoAlt: 'Schnuzzle logo',
    body: 'Scent innovation that helps dogs feel calm, engaged and genuinely stimulated. A science-led enrichment system for modern dog owners.',
    websiteUrl: 'https://schnuzzle.com/',
    logoClassName: 'max-h-14 w-[72%]',
  },
  {
    id: 'sureshot',
    name: 'SureShot',
    logoPath: '/volocean-assets/clients/sureshot-logo.png',
    logoAlt: 'SureShot logo',
    body: 'An AI-powered content platform that transforms crowd-generated videos into high-quality, shareable content for events.',
    websiteUrl: 'https://sureshot.video/',
    logoClassName: 'max-h-12 w-[82%]',
  },
  {
    id: 'toroa',
    name: 'Toroa',
    logoPath: '/volocean-assets/clients/toroa-logo.png',
    logoAlt: 'Toroa logo',
    body: 'A fintech building the infrastructure for tokenised real-world assets, offering compliant issuance, distribution, and liquidity solutions.',
    websiteUrl: 'https://www.toroa.xyz/',
    logoClassName: 'max-h-14 w-[70%]',
  },
  {
    id: 'voycap',
    name: 'Voycap',
    logoPath: '/volocean-assets/clients/voycap-logo.svg',
    logoAlt: 'Voycap logo',
    body: 'Transforming the fragmented, outdated private capital markets, where digital transformation has barely scratched the surface - creating a prime opportunity for disruption.',
    websiteUrl: 'https://www.voycap.com/',
    logoClassName: 'max-h-12 w-[80%]',
  },
];

export const VOLOCEAN_TEAM: VolOceanTeamMember[] = [
  {
    name: 'Adrien Monet',
    title: 'Associate Partner, Sales Director',
    location: 'Sydney, Australia',
    profileImages: {
      bw: '/volocean-assets/team/adrien-monet-bw.png',
      colour: '/volocean-assets/team/adrien-monet-colour.png',
      alt: 'Adrien Monet'
    },
    bio: [
      'Sales and innovation have been at the centre of his career. With nearly 15 years experience in technology, he has supported companies of all sizes to navigate change, improve performance, and unlock new opportunities.',
      'His background spans operations, sales, executive leadership and M&A, providing a broad perspective on the key drivers of growth and long-term success. At VOLOCEAN Elevate, he focuses on working with founders to bring ideas to life, scale with purpose, and build businesses that create meaningful impact.'
    ]
  },
  {
    name: 'Camille Hardman',
    title: 'Partner, US Operations, Investor Relations',
    location: 'Santa Monica, USA',
    profileImages: {
      bw: '/volocean-assets/team/camille-hardman-bw.png',
      colour: '/volocean-assets/team/camille-hardman-colour.png',
      alt: 'Camille Hardman'
    },
    bio: [
      'Camille has over 25 years of experience in the media and entertainment industry, including an Emmy Award nomination, and brings a wealth of expertise to our organisation. Her work, spanning six continents, has also focused on important environmental, social justice, and human rights issues.',
      'In addition to her media expertise, Camille has spent the last 10 years as an investor and advisor to non-profits, philanthropists, and startup companies in both the USA and her native Australia.'
    ]
  },
  {
    name: 'Charlie Streeter',
    title: 'Associate Partner, Head of Southeast Asia',
    location: 'Hong Kong',
    profileImages: {
      bw: '/volocean-assets/team/charlie-streeter-bw.png',
      colour: '/volocean-assets/team/charlie-streeter-colour.png',
      alt: 'Charlie Streeter'
    },
    bio: [
      'Charlie has over 20 years of global experience combining leadership, finance, and business development expertise to deliver sustainable revenue growth in highly competitive international markets.',
      'He is strong in advisory and mentorship roles, bringing together subject matter knowledge to assist start-ups and early-stage companies in commercialisation and sales strategies, securing financial capital, and deploying human capital to create global success stories.'
    ]
  },
  {
    name: 'Jason Holdsworth',
    title: 'Managing Partner, Advisory & Edge',
    location: 'Auckland, New Zealand',
    profileImages: {
      bw: '/volocean-assets/team/jason-holdsworth-bw.png',
      colour: '/volocean-assets/team/jason-holdsworth-colour.png',
      alt: 'Jason Holdsworth'
    },
    profileImageThumbTransform: 'translateY(2%) scale(1.04)',
    bio: [
      'Jason has over 30 years experience across enterprise software, HCM systems and workforce management technology, global market expansion, strategic advisory, and capital markets.',
      'As the Founding Partner of RITEQ, he built and scaled an enterprise workforce management software business across multiple international markets, culminating in its acquisition by Dayforce Inc. in 2019. He founded the advisory firm that became VOLOCEAN to support high-growth technology companies through capital raising, international expansion and exit transactions, prior to its merger with Ocean VC.'
    ]
  },
  {
    name: 'Mark Trowel',
    title: 'Partner, Head of Distribution',
    location: 'Sydney, Australia',
    profileImages: {
      bw: '/volocean-assets/team/mark-trowel-bw.png',
      colour: '/volocean-assets/team/mark-trowel-colour.png',
      alt: 'Mark Trowel'
    },
    profileImageThumbTransform: 'translateY(-5%) scale(1.05)',
    bio: [
      'Mark is a seasoned founder, entrepreneur and advisor with over 30 years expertise in property, banking, corporate governance and technology.',
      'He brings global experience from Macquarie Bank and multiple successful ventures, with a focus on business growth, capital markets, strategic partnerships, and go-to-market execution.'
    ]
  },
  {
    name: 'Matt Alsford',
    title: 'Associate Partner, Capital Systems & Syndication',
    location: 'Thailand',
    profileImages: {
      bw: '/volocean-assets/team/matt-alsford-bw.png',
      colour: '/volocean-assets/team/matt-alsford-colour.png',
      alt: 'Matt Alsford'
    },
    profileImageThumbTransform: 'translateY(2.5%) scale(1.06)',
    bio: [
      'Matt brings over two decades of experience founding and scaling exploration ventures across Southeast Asia and Papua New Guinea, with a focus on identifying opportunities early and backing them with conviction.',
      'His career in mining and energy has given him deep expertise in capital raising, public listings, and operating in complex emerging markets. As Asia Partner with VOLOCEAN, he applies this background to venture capital, supporting founders and investors across the region to build, scale, and exit meaningful technology companies.'
    ]
  },
  {
    name: 'Matt Chalmers',
    title: 'Associate Partner, Ecosystem & GTM Transformation',
    location: 'UK & EMEA',
    profileImages: {
      bw: '/volocean-assets/team/matt-chalmers-bw.png',
      colour: '/volocean-assets/team/matt-chalmers-colour.png',
      alt: 'Matt Chalmers'
    },
    profileImageThumbTransform: 'translateY(2%) scale(1.05)',
    bio: [
      'Matt is a multilingual, cross-functional leader with 30 years\' experience delivering transformation programs for enterprises, SMBs, and early-stage companies in a broad range of industry domains. Translating technological innovation into commercial outcomes, he has worked with startups, scale-ups, and multinational giants across the globe.',
      'Matt leverages his domain knowledge and expertise to develop new markets and channel ecosystems, accelerate time-to-value, and scale GTM operations for high-growth ventures. Transferring to technology from an early career in science, he now brings his experience to benefit VOLOCEAN and its portfolio of founders and investors.'
    ]
  },
  {
    name: 'Natalie Eisen',
    title: 'Partner, Head of Marketing & Operations',
    location: 'Sydney, Australia',
    profileImages: {
      bw: '/volocean-assets/team/natalie-eisen-bw.png',
      colour: '/volocean-assets/team/natalie-eisen-colour.png',
      alt: 'Natalie Eisen'
    },
    profileImageThumbTransform: 'translateY(1.5%) scale(1.05)',
    bio: [
      'Natalie combines founder-based operational expertise with a strong investment background. With leadership roles in real estate and venture capital, she excels in business development, investor relations, and scaling operations.',
      'She helps founders move fast with confidence and clarity.'
    ]
  },
  {
    name: 'Paul Clews',
    title: 'Group General Counsel',
    location: 'Auckland, New Zealand',
    profileImages: {
      bw: '/volocean-assets/team/paul-clews-bw.png',
      colour: '/volocean-assets/team/paul-clews-colour.png',
      alt: 'Paul Clews'
    },
    profileImageThumbTransform: 'translateY(3%) scale(1.06) rotate(3deg)',
    bio: [
      'Paul is dedicated to ensuring clients achieve their aspirations. With more than two decades in law and investment banking, he has helped hundreds of corporates raise capital and execute M&A in challenging markets across the globe.',
      'He is highly experienced in deal management and execution and focuses on ensuring clients are properly prepared and fully supported so they can maximise their potential returns.'
    ]
  },
  {
    name: 'Richard Stephenson',
    title: 'Associate Partner, GTM & International Expansion',
    location: 'Auckland, New Zealand',
    profileImages: {
      bw: '/volocean-assets/team/richard-stephenson-bw.png',
      colour: '/volocean-assets/team/richard-stephenson-colour.png',
      alt: 'Richard Stephenson'
    },
    bio: [
      'Richard has spent nearly three decades helping technology companies and global enterprises grow smarter and faster.',
      'From leading go-to-market strategies to driving transformation and operational excellence across multiple markets, he brings experience in what it takes to scale with impact. He shapes how VOLOCEAN supports founders, unlocks growth, and enters new markets.'
    ]
  },
  {
    name: 'Will Lawson',
    title: 'Managing Partner, Investment & Partnerships',
    location: 'Sydney, Australia',
    profileImages: {
      bw: '/volocean-assets/team/will-lawson-bw.png',
      colour: '/volocean-assets/team/will-lawson-colour.png',
      alt: 'Will Lawson'
    },
    profileImageObjectPosition: '50% 40%',
    profileImageThumbTransform: 'translateY(1.5%) scale(1.04)',
    bio: [
      'Will has over 30 years experience across debt, equity and FX trading, property, private equity and venture capital investment, and strategic advice consultancy.',
      'As a former Founding Partner at Seed Space Venture Capital, he has led numerous investments in high-growth companies through capital raising, growth and exits.'
    ]
  }
];

export const VOLOCEAN_LOCATIONS: VolOceanLocationItem[] = [
  {
    country: 'Australia',
    city: 'Sydney',
    addressLines: ['Suite 104, Level 1', '343 George Street', 'Sydney NSW 2000'],
    coordinates: { lat: -33.8688, lon: 151.2093 }
  },
  {
    country: 'New Zealand',
    city: 'Auckland',
    addressLines: ['Level 1, 142 Broadway', 'Newmarket', 'Auckland, 1023'],
    coordinates: { lat: -36.8485, lon: 174.7633 }
  },
  {
    country: 'Hong Kong',
    city: 'Hong Kong',
    addressLines: ['6th Floor, Cheung Hing Industrial Building', '12P Smithfield', 'Kennedy Town'],
    coordinates: { lat: 22.3193, lon: 114.1694 }
  },
  {
    country: 'United Kingdom',
    city: 'Salford / Manchester',
    addressLines: ['Alex House', 'Chapel Street', 'Salford, England, M3 5JZ'],
    coordinates: { lat: 53.4808, lon: -2.2426 }
  },
  {
    country: 'United States',
    city: 'Santa Monica / Los Angeles',
    addressLines: ['520 Broadway Suite 200', 'Santa Monica, CA 90401'],
    coordinates: { lat: 34.0195, lon: -118.4912 }
  }
];
